// DocEditor — split markdown source (lazy Monaco) / live `ChatMarkdown`
// preview, with the edit lease and the save guard wired to the proxy contract:
//
//   * entering edit mode acquires the lease (§5.1) and a 30s heartbeat renews
//     it; save, cancel, and unmount release it, and the TTL covers the rest;
//   * saving sends the lock token AND the doc's `current_revision_id`. A 409
//     `DOC_REVISION_CONFLICT` means the doc moved on, so the parent opens the
//     re-read dialog with the excerpt instead of overwriting someone's work;
//   * a lease we could not take (or lost mid-edit) shows who holds it, and
//     offers a takeover once it expires — the proxy hands an expired lease to
//     the next caller.
//
// Save / Cancel / revision summary live in a pinned top header (flex
// shrink-0) so they stay reachable while the source and preview panes
// scroll independently. Sticky is unnecessary once the page height is
// contained — the header simply does not scroll with the editor body.

import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Loader2, Lock, Save, X } from 'lucide-react'
import type { Doc } from '@axilon-apps/domains-ontology-ui-state'

import { ChatMarkdown } from '../../components/ChatMarkdown/ChatMarkdown'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { clockTime, isLeaseExpired } from './doc-helpers'
import { LazyMarkdownEditor } from './LazyMarkdownEditor'
import type { EditLease } from './use-edit-lease'

export interface DocEditorProps {
  doc: Doc
  lease: EditLease
  /** True while the save mutation is in flight. */
  saving: boolean
  /**
   * Save a revision. The parent owns the mutation so it can route a 409 to the
   * conflict dialog and refresh the doc + history on success.
   */
  onSave: (args: { content: string; summary: string }) => Promise<void>
  onCancel: () => void
  /** Bumped by the parent after a conflict reload, to reset the draft. */
  resetKey: number
  /** Called whenever dirty state changes so the parent can gate navigation. */
  onDirtyChange?: (dirty: boolean) => void
}

const DocEditor = ({
  doc,
  lease,
  saving,
  onSave,
  onCancel,
  resetKey,
  onDirtyChange
}: DocEditorProps) => {
  const [content, setContent] = useState(doc.content)
  const [summary, setSummary] = useState('')
  const lastResetRef = useRef(resetKey)

  // Only re-seed the draft from the server on an explicit reset (a conflict
  // reload); a background refetch must never clobber what the user typed.
  useEffect(() => {
    if (lastResetRef.current === resetKey) return
    lastResetRef.current = resetKey
    setContent(doc.content)
  }, [resetKey, doc.content])

  const stats = useMemo(() => {
    const lines = content === '' ? 0 : content.split('\n').length
    return `${lines} lines · ${content.length} chars`
  }, [content])

  const dirty = content !== doc.content

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])
  const blocked = lease.status === 'blocked'
  const canSave = lease.status === 'held' && !saving && dirty

  if (blocked) {
    const holder = lease.blockedBy
    const takeoverReady = holder === null || isLeaseExpired(holder.expiresAt)
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
        <AlertTriangle className="size-6 text-st-warn" aria-hidden />
        <p className="m-0 max-w-md text-sm text-ink-2">
          {holder === null
            ? (lease.error ?? 'The edit lock could not be taken.')
            : `${holder.holderKind === 'agent' ? 'Agent' : ''} ${holder.holderName} is editing this doc${
                holder.expiresAt === '' ? '' : ` until ${clockTime(holder.expiresAt)}`
              }.`.trim()}
        </p>
        <p className="m-0 max-w-md text-12 text-ink-3">
          One editor at a time. The lock always expires, so a crashed tab or a killed agent run
          never blocks the doc for good.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}>
            Back to reading
          </Button>
          <Button size="sm" disabled={!takeoverReady} onClick={lease.retry}>
            <Lock aria-hidden />
            {takeoverReady ? 'Take over editing' : 'Retry'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="z-10 flex shrink-0 items-center gap-2 border-b border-line bg-bg-elev px-3.5 py-2">
        <Input
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="What changed? (optional revision summary)"
          aria-label="Revision summary"
          className="h-8 max-w-md text-12"
        />
        <span className="min-w-0 flex-1 truncate text-11 text-ink-3">
          {lease.status === 'acquiring'
            ? 'Taking the edit lock…'
            : 'Saving credits the revision to you — the agent keeps its own lineage.'}
        </span>
        <Button size="sm" variant="outline" disabled={saving} onClick={onCancel}>
          <X aria-hidden />
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!canSave}
          title={dirty ? undefined : 'No changes to save yet.'}
          onClick={() => void onSave({ content, summary })}
        >
          {saving ? <Loader2 className="animate-spin" aria-hidden /> : <Save aria-hidden />}
          Save revision
        </Button>
      </header>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col border-r border-line">
          <div className="flex shrink-0 items-baseline gap-2 border-b border-line px-3.5 py-1.5">
            <span className="text-11 font-semibold uppercase tracking-wider text-ink-3">
              Markdown
            </span>
            <div className="flex-1" />
            <span className="font-mono text-11 text-ink-3">{stats}</span>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            <LazyMarkdownEditor value={content} onChange={setContent} />
          </div>
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-line px-3.5 py-1.5">
            <span className="text-11 font-semibold uppercase tracking-wider text-ink-3">
              Preview
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
            <ChatMarkdown className="text-sm text-ink-2">{content}</ChatMarkdown>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocEditor
